import { useState, useEffect } from 'react';
import type { VendorInfo, TermListItem, CurrencyListItem } from '@acitra/shared';
import { createVendor, listTerms, listCurrencies, type VendorListItem } from '../../services/api';
import { InfoTooltip } from '../InfoTooltip';

interface CreateVendorFormProps {
  tenantId: string;
  extractedVendor?: VendorInfo;
  onVendorCreated: (vendor: VendorListItem) => void;
  onCancel: () => void;
}

export function CreateVendorForm({
  tenantId,
  extractedVendor,
  onVendorCreated,
  onCancel,
}: CreateVendorFormProps) {
  const [displayName, setDisplayName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');

  // QuickBooks defaults
  const [selectedTermId, setSelectedTermId] = useState('');
  const [selectedCurrencyId, setSelectedCurrencyId] = useState('');
  const [terms, setTerms] = useState<TermListItem[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyListItem[]>([]);
  const [isLoadingTerms, setIsLoadingTerms] = useState(true);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(true);
  const [isTermsDropdownOpen, setIsTermsDropdownOpen] = useState(false);
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill from extracted vendor
  useEffect(() => {
    if (extractedVendor) {
      setDisplayName(extractedVendor.name || '');
      setCompanyName(extractedVendor.name || '');
      setEmail(extractedVendor.email || '');
      setPhone(extractedVendor.phone || '');
      setAccountNumber(extractedVendor.accountNumber || '');

      if (extractedVendor.address) {
        setAddressLine1(extractedVendor.address.line1 || '');
        setAddressLine2(extractedVendor.address.line2 || '');
        setCity(extractedVendor.address.city || '');
        setState(extractedVendor.address.state || '');
        setPostalCode(extractedVendor.address.postalCode || '');
        setCountry(extractedVendor.address.country || '');
      }
    }
  }, [extractedVendor]);

  // Load terms and currencies
  useEffect(() => {
    async function loadTerms() {
      try {
        const data = await listTerms(tenantId, { active: true });
        setTerms(data);
      } catch (err) {
        console.error('Failed to load terms:', err);
      } finally {
        setIsLoadingTerms(false);
      }
    }

    async function loadCurrencies() {
      try {
        const data = await listCurrencies(tenantId, { active: true });
        setCurrencies(data);
      } catch (err) {
        console.error('Failed to load currencies:', err);
      } finally {
        setIsLoadingCurrencies(false);
      }
    }

    loadTerms();
    loadCurrencies();
  }, [tenantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const hasAddress = addressLine1 || addressLine2 || city || state || postalCode || country;

      const vendor = await createVendor(tenantId, {
        displayName: displayName.trim(),
        companyName: companyName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        accountNumber: accountNumber.trim() || undefined,
        address: hasAddress
          ? {
              line1: addressLine1.trim() || undefined,
              line2: addressLine2.trim() || undefined,
              city: city.trim() || undefined,
              state: state.trim() || undefined,
              postalCode: postalCode.trim() || undefined,
              country: country.trim() || undefined,
            }
          : undefined,
        termId: selectedTermId || undefined,
        currencyId: selectedCurrencyId || undefined,
      });

      onVendorCreated(vendor);
    } catch (err) {
      console.error('Failed to create vendor:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to create vendor. The display name may already exist in QuickBooks.'
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to search
        </button>
      </div>

      <div className="space-y-2">
        {/* Display Name - Required */}
        <div>
          <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
            Display Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            placeholder="How the vendor appears in QuickBooks"
            required
          />
        </div>

        {/* Company Name */}
        <div>
          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            id="companyName"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
        </div>

        {/* Email and Phone - Side by side */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
        </div>

        {/* Account Number */}
        <div>
          <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 mb-1">
            Account Number
          </label>
          <input
            type="text"
            id="accountNumber"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            placeholder="Your account number with this vendor"
          />
        </div>

        {/* Payment Terms and Currency */}
        <div className="grid grid-cols-2 gap-4">
          {/* Payment Terms */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
            <button
              type="button"
              onClick={() => setIsTermsDropdownOpen(!isTermsDropdownOpen)}
              disabled={isLoadingTerms}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
            >
              <span className={selectedTermId ? 'text-gray-900' : 'text-gray-500'}>
                {isLoadingTerms
                  ? 'Loading...'
                  : selectedTermId
                    ? terms.find((t) => t.quickbooksId === selectedTermId)?.name || 'None'
                    : 'None'}
              </span>
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform ${isTermsDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {isTermsDropdownOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setIsTermsDropdownOpen(false)} />
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTermId('');
                      setIsTermsDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${!selectedTermId ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                  >
                    None
                  </button>
                  {terms.map((term) => (
                    <button
                      key={term.id}
                      type="button"
                      onClick={() => {
                        setSelectedTermId(term.quickbooksId);
                        setIsTermsDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${selectedTermId === term.quickbooksId ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                    >
                      {term.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Currency */}
          <div className="relative">
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              Currency
              {currencies.length <= 1 && !isLoadingCurrencies && (
                <InfoTooltip
                  text="Multi-currency is not enabled in your QuickBooks company."
                  width="w-52"
                />
              )}
            </label>
            {currencies.length <= 1 && !isLoadingCurrencies ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 text-sm cursor-not-allowed">
                {currencies[0] ? currencies[0].code : 'Default'}
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                  disabled={isLoadingCurrencies}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                >
                  <span className={selectedCurrencyId ? 'text-gray-900' : 'text-gray-500'}>
                    {isLoadingCurrencies
                      ? 'Loading...'
                      : selectedCurrencyId
                        ? currencies.find((c) => c.quickbooksId === selectedCurrencyId)?.code ||
                          'Default'
                        : 'Default'}
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${isCurrencyDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isCurrencyDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsCurrencyDropdownOpen(false)}
                    />
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCurrencyId('');
                          setIsCurrencyDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${!selectedCurrencyId ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                      >
                        Default
                      </button>
                      {currencies.map((currency) => (
                        <button
                          key={currency.id}
                          type="button"
                          onClick={() => {
                            setSelectedCurrencyId(currency.quickbooksId);
                            setIsCurrencyDropdownOpen(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${selectedCurrencyId === currency.quickbooksId ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                        >
                          {currency.code}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Address Section */}
        <div className="pt-2">
          <p className="text-sm font-medium text-gray-700 mb-2">Billing Address</p>
          <div className="space-y-3">
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="Street address"
            />
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="Apt, suite, unit, building, floor, etc."
            />
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-4">
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="City"
                />
              </div>
              <div className="col-span-2">
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="State"
                />
              </div>
              <div className="col-span-3">
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="ZIP/Postal"
                />
              </div>
              <div className="col-span-3">
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="Country"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Submit button */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isCreating || !displayName.trim()}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCreating ? 'Creating...' : 'Create & Match Vendor'}
        </button>
      </div>
    </form>
  );
}
